import React, { FC } from 'react';
import { ERASER_TOOL } from '@v2/components/imageHandling/components/DrawingCanvas/constants';
import AnnotationLayer from '@v2/components/imageHandling/components/DrawingCanvas/components/AnnotationLayer';
import TextInputOverlay from '@v2/components/imageHandling/components/DrawingCanvas/components/TextInputOverlay';
import { useOptionalDrawingSession } from '@v2/components/imageHandling/components/DrawingCanvas/DrawingSessionContext';

interface AnnotationOverlayProps {
  /** When true, AnnotationLayer's native branch skips its own zoom/pan transform — used when the
   *  overlay is mounted inside Zoomable, which already applies the image's transform. */
  insideZoomable?: boolean;
  /** Web only — wires the controller's wheel/dblclick listener to the overlay's outer div. */
  bindControllerRef?: boolean;
  /** Web-only — render `ZoomControls` inline for the desktop drawing surface (passed by DrawingCanvas's web chrome). */
  zoomControls?: React.ReactNode;
}

/**
 * The annotation surface — SVG layer + inline text input. State comes from the lifted
 * DrawingSession context; this component is rendered both inside Picture's Zoomable on native
 * (via the `zoomableOverlay` slot) AND inside DrawingCanvas's drawing surface on web (legacy
 * inline path).
 */
const AnnotationOverlay: FC<AnnotationOverlayProps> = ({ insideZoomable, bindControllerRef, zoomControls }) => {
  const session = useOptionalDrawingSession();
  if (!session) return null;

  const {
    api,
    pointer,
    textEditing,
    editingAnnotation,
    transformInputs,
    cursor,
    viewTransform,
    handlePointerDown,
    handleDoubleClick,
    transformController,
    containerRect,
    svgCtm,
    layout,
    setLayout,
    hitThreshold,
  } = session;

  return (
    <>
      <AnnotationLayer
        annotations={api.annotations}
        draftAnnotation={pointer.draftAnnotation}
        selectedId={api.selectedId}
        hoveredAnnotationId={pointer.hoveredAnnotationId}
        eraserHoverId={api.activeTool === ERASER_TOOL ? pointer.hoveredAnnotationId : null}
        editingTextId={textEditing.editing?.annotationId ?? null}
        editingText={textEditing.editing ? textEditing.textValue : null}
        transformInputs={transformInputs}
        cursor={cursor}
        isAnnotating
        viewTransform={viewTransform}
        onPointerDown={handlePointerDown}
        onPointerMove={pointer.handlePointerMove}
        onPointerUp={pointer.handlePointerUp}
        onDoubleClick={handleDoubleClick}
        webContainerRefExternal={bindControllerRef ? transformController.containerRef : undefined}
        insideZoomable={insideZoomable}
        onViewportLayout={setLayout}
        hitThreshold={hitThreshold}
      />
      <TextInputOverlay
        editing={textEditing.editing}
        annotation={editingAnnotation}
        value={textEditing.textValue}
        onChange={textEditing.updateText}
        onCommit={textEditing.commit}
        onCancel={textEditing.cancel}
        inputRef={textEditing.inputRef}
        containerRect={containerRect}
        svgCtm={svgCtm}
        viewport={layout}
        viewTransform={viewTransform}
      />
      {zoomControls}
    </>
  );
};

export default AnnotationOverlay;
